/*
 * R159ss · Feb 2026 — Assign Outside Member helper.
 *
 * When a user picks an assignee who isn't already a member of the
 * task's project, we surface a small chooser dialog with 3 actions:
 *   • Add + assign   (POST /projects/{pid}/members, then PATCH task)
 *   • Assign anyway  (PATCH task only — user still won't have access,
 *                     but the assignment is recorded and the row will
 *                     render in red-bold as a visual warning)
 *   • Cancel
 *
 * Consumers use `useAssignOutsideMember()` to get a `guardedAssign()`
 * function + the JSX for the dialog. The hook is intentionally light
 * so every existing picker (Planning Board inline, Task Detail,
 * Present mode) can wire it in without a big refactor.
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

export function useAssignOutsideMember({ onAfterAssign } = {}) {
  const [pending, setPending] = useState(null); // { taskId, projectId, userId, userName, onAssigned }
  const [busy, setBusy] = useState(false);

  /**
   * Wrap an assignment. If the target user is already a project
   * member, PATCHes the task directly. Otherwise opens the dialog.
   *
   * @param {object} args
   * @param {string} args.taskId
   * @param {string} args.projectId
   * @param {string|null} args.userId     (null clears the assignee)
   * @param {string|null} args.userName
   * @param {string[]} args.projectMemberIds
   * @param {Function} [args.onAssigned]  (patchedTask) => void
   */
  const guardedAssign = useCallback(async ({ taskId, projectId, userId, userName, projectMemberIds, onAssigned }) => {
    // Clearing / no project reference / already a member → direct patch.
    if (!userId || !projectId || (projectMemberIds || []).includes(userId)) {
      try {
        const { data } = await api.patch(`/tasks/${taskId}`, { assignee_id: userId || null });
        toast.success(userName ? `Reassigned to ${userName}` : "Assignee cleared");
        onAssigned && onAssigned(data);
        onAfterAssign && onAfterAssign(data);
      } catch (e) {
        toast.error(apiErrorMessage(e, "Reassign failed"));
      }
      return;
    }
    // Not a member → open the chooser.
    setPending({ taskId, projectId, userId, userName, onAssigned });
  }, [onAfterAssign]);

  const _addAndAssign = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      // 1. Add to project (idempotent — server upserts if already present).
      try {
        await api.post(`/projects/${pending.projectId}/members`, {
          user_id: pending.userId,
        });
      } catch (e) {
        // Silently accept 409/duplicate — a race with another admin.
        const status = e?.response?.status;
        if (status && status !== 409) throw e;
      }
      // 2. PATCH task
      const { data } = await api.patch(`/tasks/${pending.taskId}`, { assignee_id: pending.userId });
      toast.success(`${pending.userName || "User"} added to project + assigned`);
      pending.onAssigned && pending.onAssigned(data);
      onAfterAssign && onAfterAssign(data);
      setPending(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't add member"));
    } finally { setBusy(false); }
  };

  const _assignAnyway = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/tasks/${pending.taskId}`, { assignee_id: pending.userId });
      toast.warning(`${pending.userName || "User"} assigned — but they're not on the project. They won't have access until added.`);
      pending.onAssigned && pending.onAssigned(data);
      onAfterAssign && onAfterAssign(data);
      setPending(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reassign failed"));
    } finally { setBusy(false); }
  };

  const dialog = pending ? (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) setPending(null); }}>
      <DialogContent className="max-w-sm" data-testid="assign-outside-member-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> Not on this project
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-foreground">{pending.userName || "This user"}</span> is not a member of
            this project. Without membership they won&rsquo;t be able to open the task.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-muted-foreground border-l-2 border-amber-400 bg-amber-50 px-2 py-1.5">
          Pick how you want to proceed. &ldquo;Assign anyway&rdquo; will still record the
          assignment but the row will be flagged in red across the app so it can be
          fixed later.
        </div>
        <DialogFooter className="gap-1 flex-wrap">
          <Button variant="ghost" onClick={() => setPending(null)} disabled={busy}
                  className="rounded-none" data-testid="assign-outside-cancel">
            Cancel
          </Button>
          <Button variant="outline" onClick={_assignAnyway} disabled={busy}
                  className="rounded-none" data-testid="assign-outside-assign-anyway">
            Assign anyway
          </Button>
          <Button onClick={_addAndAssign} disabled={busy}
                  className="rounded-none" data-testid="assign-outside-add-and-assign">
            {busy ? "…" : "Add + assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null;

  return { guardedAssign, dialog };
}
